import React, { useEffect, useState, useRef } from "react";
import Box from "@mui/material/Box";
import { styled } from "@mui/material/styles";
import CustomSeparator from "../../layouts/AdminLayout/Header/Breadcrumbs.js";
import { Typography } from "@mui/material";
import Stack from "@mui/material/Stack";
import {
  Form,
  Input,
  InputNumber,
  Popconfirm,
  Table,
  Typography as Typographys,
  Button as Buttons,
  notification,
  Tag,
  Select,
  Space,
} from "antd";
import type { SelectProps } from "antd";
import InvoiceController from "./Controller/invoice_controller.ts";
import {
  UploadOutlined,
  DeleteOutlined,
  EditOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import Button from "@mui/material/Button";
import List from "@mui/material/List";
import Menu from "@mui/material/Menu";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import Checkbox from "@mui/material/Checkbox";
import ListItemText from "@mui/material/ListItemText";
import { render } from "@testing-library/react";
import type { InputRef, TableColumnType } from "antd";
import type { FilterDropdownProps } from "antd/es/table/interface";
import Highlighter from "react-highlight-words";

type NotificationType = "success" | "info" | "warning" | "error";

const { TextArea } = Input;
const { Text } = Typographys;

const DrawerHeader = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-end",
  padding: theme.spacing(0, 1),
  // necessary for content to be below app bar
  ...theme.mixins.toolbar,
}));

interface DataType {
  key: string;
  member_feeID: string;
  invoice_notes: any;
  description: string;
  invoice_noteID: string;
  amount: number;
  label: string;
  invoice_noteIDs: any;
}

type DataIndex = keyof DataType;

interface EditableCellProps extends React.HTMLAttributes<HTMLElement> {
  editing: boolean;
  dataIndex: string;
  title: any;
  inputType: "number" | "text" | "description" | "invoice_noteIDs" | "amount";
  record: DataType;
  index: number;
  options: any;
}

const EditableCell: React.FC<React.PropsWithChildren<EditableCellProps>> = ({
  editing,
  dataIndex,
  title,
  inputType,
  record,
  index,
  children,
  options,
  ...restProps
}) => {
  let inputNode: any;
  if (inputType === "description") {
    inputNode = <TextArea rows={4} />;
  } else if (inputType === "invoice_noteIDs") {
    inputNode = (
      <Select
        mode="multiple"
        allowClear
        style={{ width: "100%" }}
        placeholder="Please select"
        options={options}
      />
    );
  } else if (inputType === "amount") {
    inputNode = (
      <InputNumber<number>
        defaultValue={1000}
        step={0.01} // Allows increment and decrement in steps of 0.01
        formatter={(value) =>
          `RM ${Number(value || 0)
            .toFixed(2)
            .replace(/\B(?=(\d{3})+(?!\d))/g, ",")}`
        }
        parser={(value) => parseFloat(value?.replace(/[^0-9.]/g, "") || "0")}
        onChange={(value) => console.log(value)} // Adjust your onChange function as needed
      />
    );
  } else {
    inputNode = <TextArea rows={4} />;
  }
  return (
    <td {...restProps}>
      {editing ? (
        <Form.Item
          name={dataIndex}
          style={{ margin: 0 }}
          initialValue={
            inputType === "invoice_noteIDs"
              ? record?.invoice_notes?.map(
                  (item: any) => item.invoice_noteID
                ) || []
              : record[dataIndex]
          } // Set initial value for controlled fields
          rules={[
            {
              required: true,
              message: `Please Input ${title}!`,
            },
          ]}
        >
          {inputNode}
        </Form.Item>
      ) : (
        children
      )}
    </td>
  );
};

const CheckboxList = ({ baseColumns, checkedColumns, onChange }) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleToggle = (value: Number) => () => {
    const currentIndex = checkedColumns.indexOf(value);
    const newChecked = [...checkedColumns];

    if (currentIndex === -1) {
      newChecked.push(value);
    } else {
      newChecked.splice(currentIndex, 1);
    }

    onChange(newChecked); // Call the parent onChange handler
  };

  return (
    <>
      <Button
        id="demo-positioned-button"
        aria-controls={open ? "demo-positioned-menu" : undefined}
        aria-haspopup="true"
        aria-expanded={open ? "true" : undefined}
        onClick={handleClick}
        variant="contained"
        style={{ backgroundColor: "#d33333" }}
      >
        Columns
      </Button>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        PaperProps={{
          style: {
            // maxHeight: 400,
            width: "20ch",
          },
        }}
      >
        <List
          sx={{ width: "100%", maxWidth: 360, bgcolor: "background.paper" }}
        >
          {baseColumns.map((value) => {
            const isChecked = checkedColumns.includes(value.dataIndex);

            const labelId = `checkbox-list-label-${value.dataIndex}`;
            return (
              <ListItem key={value.dataIndex} disablePadding>
                <ListItemButton
                  role={undefined}
                  onClick={handleToggle(value.dataIndex)}
                  dense
                >
                  <ListItemIcon>
                    <Checkbox
                      edge="start"
                      checked={isChecked}
                      tabIndex={-1}
                      disableRipple
                      inputProps={{ "aria-labelledby": labelId }}
                    />
                  </ListItemIcon>
                  <ListItemText id={labelId} primary={value.title} />
                </ListItemButton>
              </ListItem>
            );
          })}
        </List>
      </Menu>
    </>
  );
};

const formatDate = (dateString) => {
  const date = new Date(dateString); // Convert to Date object
  return date.toLocaleString("en-US", {
    weekday: "long", // Example: Friday
    year: "numeric", // Example: 2024
    month: "long", // Example: November
    day: "numeric", // Example: 29
    hour: "numeric", // Example: 9
    minute: "numeric", // Example: 30
  });
};

const App: React.FC = () => {
  const [data, setData] = useState<DataType[]>([]);
  const [shouldRefetchData, setShouldRefetchData] = useState(false);
  const [invoiceNoteFilter, setInvoiceNoteFilter] = useState([]);

  useEffect(() => {
    InvoiceController.getMemberFees().then((response) => {
      const row = response.map((item: any, index: number) => {
        console.log(item);
        return {
          key: index,
          member_feeID: item.member_feeID,
          label: item.label,
          description: item.description,
          amount: item.amount,
          invoice_notes: item.invoice_notes,
          invoice_noteIDs: item.invoice_noteIDs,
        };
      });

      setData(row);

      InvoiceController.getInvoiceNote().then((response) => {
        // Transform the API response to match the filters pattern
        const filters = response.map((item) => ({
          text: item.title, // Use the appropriate key for the display text
          value: item.invoice_noteID, // Use the appropriate key for the filter value
        }));

        setInvoiceNoteFilter(filters);
      });
    });
  }, [shouldRefetchData]);

  const [api, contextHolder] = notification.useNotification();

  const openNotificationWithIcon = (
    type: NotificationType,
    desc: string,
    message: String
  ) => {
    api[type]({
      message: message,
      description: message,
    });
  };

  const [form] = Form.useForm();

  const [editingKey, setEditingKey] = useState("");

  const isEditing = (record: DataType) => record.key === editingKey;

  const edit = (record: Partial<DataType> & { key: React.Key }) => {
    setEditingKey(record.key);
  };

  const cancel = () => {
    setEditingKey("");
  };

  const save = async (key: React.Key) => {
    try {
      const row = (await form.validateFields()) as DataType;
      console.log(row);
      const newData = [...data];
      const index = newData.findIndex((item) => key === item.key);

      const member_feeID = newData[index].member_feeID;
      console.log(member_feeID);

      if (index > -1) {
        const payload = new FormData();
        payload.append("ID", member_feeID); // Append event ID

        Object.keys(row).forEach((field) => {
          payload.append(`data[${field}]`, row[field]); // Add form fields to payload
        });

        InvoiceController.editMemberFee(payload).then((response) => {
          console.log(response);
          if (response?.status === "success") {
            openNotificationWithIcon(
              "success",
              "Your data has been saved successfully!",
              "Success"
            );
            setEditingKey("");
            setShouldRefetchData((prev) => !prev); // Toggle to re-trigger useEffect
          }
        });
      } else {
        newData.push(row);
        setData(newData);
        setEditingKey("");
      }
    } catch (errInfo) {
      console.log("Validate Failed:", errInfo);
    }
  };

  // **************************** SEARCH FILTER ANTD TABLE *****************************************************
  const [searchText, setSearchText] = useState("");
  const [searchedColumn, setSearchedColumn] = useState("");
  const searchInput = useRef<InputRef>(null);

  const handleSearch = (
    selectedKeys: string[],
    confirm: FilterDropdownProps["confirm"],
    dataIndex: DataIndex
  ) => {
    confirm();
    setSearchText(selectedKeys[0]);
    setSearchedColumn(dataIndex);
  };

  const handleReset = (clearFilters: () => void) => {
    clearFilters();
    setSearchText("");
  };

  const getColumnSearchProps = (
    dataIndex: DataIndex
  ): TableColumnType<DataType> => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
      close,
    }) => (
      <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
        <Input
          ref={searchInput}
          placeholder={`Search ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={(e) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() =>
            handleSearch(selectedKeys as string[], confirm, dataIndex)
          }
          style={{ marginBottom: 8, display: "block" }}
        />
        <Space>
          <Buttons
            type="primary"
            onClick={() =>
              handleSearch(selectedKeys as string[], confirm, dataIndex)
            }
            icon={<SearchOutlined />}
            size="small"
            style={{ width: 90 }}
          >
            Search
          </Buttons>
          <Buttons
            onClick={() => clearFilters && handleReset(clearFilters)}
            size="small"
            style={{ width: 90 }}
          >
            Reset
          </Buttons>
          <Buttons
            type="link"
            size="small"
            onClick={() => {
              confirm({ closeDropdown: false });
              setSearchText((selectedKeys as string[])[0]);
              setSearchedColumn(dataIndex);
            }}
          >
            Filter
          </Buttons>
          <Buttons
            type="link"
            size="small"
            onClick={() => {
              close();
            }}
          >
            close
          </Buttons>
        </Space>
      </div>
    ),
    filterIcon: (filtered: boolean) => (
      <SearchOutlined style={{ color: filtered ? "#1677ff" : undefined }} />
    ),
    onFilter: (value, record) =>
      record[dataIndex]
        ? record[dataIndex]
            .toString()
            .toLowerCase()
            .includes((value as string).toLowerCase())
        : false,
    onFilterDropdownOpenChange: (open) => {
      if (open) {
        setTimeout(() => searchInput.current?.select(), 100);
      }
    },
    render: (text) =>
      searchedColumn === dataIndex ? (
        <Highlighter
          highlightStyle={{ backgroundColor: "#ffc069", padding: 0 }}
          searchWords={[searchText]}
          autoEscape
          textToHighlight={text ? text.toString() : ""}
        />
      ) : (
        text
      ),
  });
  // ************************************************************************************************************

  const columns = [
    {
      title: "ID",
      dataIndex: "member_feeID",
      width: "10%",
      ...getColumnSearchProps("member_feeID"),
    },
    {
      title: "Label",
      dataIndex: "label",
      width: "20%",
      ...getColumnSearchProps("label"),
    },
    {
      title: "Description",
      dataIndex: "description",
      width: "20%",
      editable: true,
      ...getColumnSearchProps("description"),
    },
    {
      title: "Amount",
      dataIndex: "amount",
      width: "10%",
      editable: true,
      render: (data: any) => {
        return (
          <Text>
            RM
            {data.toLocaleString("en-US", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
          </Text>
        );
      },
    },
    {
      title: "Invoice Notes",
      dataIndex: "invoice_noteIDs",
      width: "35%",
      editable: true,
      render: (data: any, record: DataType) => {
        return (
          <>
            {record.invoice_notes.map((item, index) => (
              <Tag key={index} color="#108ee9">
                {item.title}
              </Tag>
            ))}
          </>
        );
      },
      filters: invoiceNoteFilter,
      onFilter: (value: any, record: any) => {
        const invoiceNoteArray = record.invoice_noteIDs
          .split(",")
          .map((id: string) => id.trim().toLowerCase());

        return invoiceNoteArray.includes(value.toLowerCase());
      },
    },
    {
      title: "operation",
      dataIndex: "operation",
      render: (_: any, record: DataType) => {
        const editable = isEditing(record);
        return editable ? (
          <span>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                width: "auto",
              }}
            >
              <Buttons
                onClick={() => save(record.key)}
                color="default"
                variant="solid"
                style={{ marginBottom: "10px" }}
              >
                Save
              </Buttons>
              <Buttons color="default" variant="outlined">
                <Popconfirm title="Sure to cancel?" onConfirm={cancel}>
                  Cancel
                </Popconfirm>
              </Buttons>
            </div>
          </span>
        ) : (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              width: "auto",
            }}
          >
            <Buttons
              color="default"
              variant="solid"
              icon={<EditOutlined />}
              disabled={editingKey !== ""}
              onClick={() => edit(record)}
              style={{ marginBottom: "10px" }}
            >
              Edit
            </Buttons>
          </div>
        );
      },
    },
  ];

  const [checkedColumns, setCheckedColumns] = useState([
    "member_feeID",
    "invoice_noteIDs",
    "label",
    "description",
    "amount",
    "operation",
  ]);

  const onChange = (checkedValues) => {
    setCheckedColumns(checkedValues);
  };

  useEffect(() => {
    // Fetch invoice notes and set options
    InvoiceController.getInvoiceNote().then((response) => {
      const options = response.map((item) => ({
        label: item.title, // Adjust the property name as per your API response
        value: item.invoice_noteID, // Adjust the property name as per your API response
      }));
      setInvoiceNotesOptions(options); // Store the options in state
    });
  }, []);

  const [invoiceNotesOptions, setInvoiceNotesOptions] = useState([]);

  const mergedColumns = columns
    .filter(
      (col) =>
        typeof col.dataIndex === "string" &&
        checkedColumns.includes(col.dataIndex)
    )
    .map((col) => {
      if (!col.editable) {
        return col;
      }
      return {
        ...col,
        onCell: (record: DataType) => {
          const columnTitle =
            typeof col.title === "string" ? col.title : String(col.title);
          const columnDataIndex =
            typeof col.dataIndex === "string"
              ? col.dataIndex
              : String(col.dataIndex);

          return {
            record,
            inputType:
              col.dataIndex === "description"
                ? "description"
                : col.dataIndex === "label"
                ? "label"
                : col.dataIndex === "invoice_noteIDs"
                ? "invoice_noteIDs"
                : col.dataIndex === "amount"
                ? "amount"
                : "text", // Update to set 'date' for date columns
            dataIndex: columnDataIndex,
            title: columnTitle,
            editing: isEditing(record),
            options:
              col.dataIndex === "invoice_noteIDs"
                ? invoiceNotesOptions
                : undefined,
          };
        },
      };
    });

  return (
    <>
      <Box
        sx={{
          display: "flex",
          backgroundColor: "#e8e8e8",
          height: "100%", // scroll y for page
          minHeight: "150vh",
          width: "100vw", // Full viewport width
          overflow: "hidden", // Ensure no overflow from the Box itself
        }}
      >
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            p: 3,
            display: "flex", // Ensures the table fills the remaining space
            flexDirection: "column",
            height: "100%", // Fill remaining height
            width: "100%", // Fill remaining width
          }}
        >
          <DrawerHeader />

          {contextHolder}

          <CustomSeparator pageName="invoice_notes" />

          <Typography
            gutterBottom
            variant="h4"
            sx={{ color: "text.secondary" }}
          >
            Invoice Notes Directory
          </Typography>

          <Stack spacing={2} direction="row" sx={{ mb: 3 }}>
            <CheckboxList
              baseColumns={columns}
              checkedColumns={checkedColumns}
              onChange={onChange}
            />
          </Stack>

          {/* Box to hold the table with full width/height */}
          <Box
            sx={{
              flexGrow: 1, // Ensures the table box expands
              backgroundColor: "#fff", // Set the background color for the table container
              height: "100%", // Ensures the table fills available height
              width: "100%", // Ensures the table fills available width
              overflow: "auto", // Allow scrolling if needed
              borderRadius: "8px", // Optional: for rounded corners
            }}
          >
            <Form form={form} component={false}>
              <Table<DataType>
                components={{
                  body: { cell: EditableCell },
                }}
                bordered
                dataSource={data}
                columns={mergedColumns}
                rowClassName="editable-row"
                pagination={{ onChange: cancel }}
              />
            </Form>
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default App;
